import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { unregisterFcmToken } from "../services/fcm";
import "./Navbar.css";

const BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

// Socket created once outside the component so it persists across renders.
const socket = io(BASE_URL, { transports: ["websocket", "polling"] });

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();

  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const username = localStorage.getItem("username");
  const profilePic = localStorage.getItem("profilePic");
  const userId = localStorage.getItem("userId");

  // UNREAD NOTIFICATION BADGE
  // Fetch the authoritative unread count once, then keep it live:
  //  - a new notification arrives over the socket  → increment
  //  - the notifications page marks everything read → reset to 0
  useEffect(() => {
    if (!userId) return;

    let active = true;

    const fetchUnread = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/api/notifications/${userId}/unread-count`
        );
        const data = await res.json();
        if (active) setUnreadCount(data.unreadCount || 0);
      } catch (err) {
        console.log(err);
      }
    };

    fetchUnread();

    // Join personal room so the server can target this user.
    socket.emit("join", userId);

    const handleNew = (data) => {
      const targetId = data?.userId || data?.receiverId || data?.toUserId;
      // Only count notifications addressed to this user. Don't bump the
      // badge while the user is already viewing the notifications page.
      if (targetId && targetId.toString() !== userId) return;
      if (window.location.pathname === "/notify") return;
      setUnreadCount((c) => c + 1);
    };

    const handleRead = () => setUnreadCount(0);

    socket.on("newNotification", handleNew);
    socket.on("notification", handleNew);
    socket.on("new_notification", handleNew);
    socket.on("statusNotification", handleNew);
    window.addEventListener("notifications-read", handleRead);

    return () => {
      active = false;
      socket.off("newNotification", handleNew);
      socket.off("notification", handleNew);
      socket.off("new_notification", handleNew);
      socket.off("statusNotification", handleNew);
      window.removeEventListener("notifications-read", handleRead);
    };
  }, [userId]);

  // SCROLL EFFECT
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // CLOSE MOBILE MENU ON ROUTE CHANGE
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // PREVENT BODY SCROLL WHEN MOBILE MENU OPEN
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  const handleLogout = async () => {
    // Remove this device's push token from the backend FIRST, while the JWT
    // in localStorage is still present to authorise the call. Never blocks
    // logout — any failure is swallowed inside unregisterFcmToken.
    await unregisterFcmToken();
    localStorage.clear();
    navigate("/");
    window.location.reload();
  };

  const navLinks = [
    { to: "/home", label: "Home", icon: "🏠" },
    { to: "/add-blog", label: "Write Blogs", icon: "✍️" },
    { to: "/blogs", label: "Read Blogs", icon: "📖" },
    { to: "/pro", label: "Profile", icon: "👤" },
    { to: "/leaderboard", label: "LeaderBoard", icon: "🏆" },
    { to: "/ad", label: "Advertise", icon: "📢" },
    // LoveSphere opens the Live Leaderboard directly — the "Love & Friendship
    // Creator" page is no longer a stop on the way.
    { to: "/lovelist", label: "LoveSphere", icon: "💑" },
    { to: "/today", label: "Reward", icon: "💎" },




   
  /* { 
  to: "/view-status", label: "View Status", icon: "👀" },*/
  
     { to: "/notify", label: "Notification", icon: "🔔" },
  ];

  return (
    <>
      <nav className={`navbar-container ${scrolled ? "scrolled" : ""}`}>

        {/* BACKGROUND EFFECTS */}
        <div className="navbar-orb orb1"></div>
        <div className="navbar-orb orb2"></div>

        {/* LOGO */}
        <Link to="/home" className="navbar-logo">
          <div className="logo-icon">
            <img src="/blog.png" alt="logo" />
          </div>

          <span className="logo-text">
            The<span className="logo-accent">Blogs</span>Sphere
          </span>
        </Link>

        {/* MOBILE MENU BUTTON */}
        <button
          className={`hamburger ${isMobileMenuOpen ? "open" : ""}`}
          onClick={toggleMobileMenu}
          aria-label="Menu"
        >
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </button>

        {/* NAVIGATION */}
        <div
          className={`navbar-links ${
            isMobileMenuOpen ? "mobile-open" : ""
          }`}
        >
          

          {/* NAV LINKS */}
          {navLinks.map((link, index) => {
            const isActive = location.pathname === link.to;

            return (
              <Link
                key={link.to}
                to={link.to}
                className={`nav-link ${isActive ? "active" : ""}`}
                style={{
                  animationDelay: `${index * 0.05}s`,
                }}
              >
                <span className="nav-icon">
                  {link.icon}

                  {/* Unread notification badge — only on the bell, only > 0 */}
                  {link.to === "/notify" && unreadCount > 0 && (
                    <span className="nav-notif-badge">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </span>

                <span className="nav-label">{link.label}</span>

                {isActive && <span className="active-dot"></span>}
              </Link>
            );
          })}

          {/* LOGOUT */}
          <button
            className="logout-btn"
            onClick={handleLogout}
          >
            <span className="logout-icon">⏻</span>
            <span>Logout</span>
          </button>
        </div>
      </nav>

      {/* MOBILE OVERLAY */}
      <div
        className={`mobile-overlay ${
          isMobileMenuOpen ? "show-overlay" : ""
        }`}
        onClick={() => setMobileMenuOpen(false)}
      ></div>
    </>
  );
}

export default Navbar;